import { SearchRequest } from 'gql/autogen/SearchRequest';
import { mapCityIdForSearch } from 'gql/service/graph/activityService';
import { ActivityGqlOperation, GqlLogTags, GqlResponseError, OperationKey, SearchRequestProps, SearchRequestType } from 'store/api/api.types';
import { InternalContext } from 'gql/autogen/InternalContext';
import { RootState } from 'store/store.types';
import { DefaultRequestProps } from 'store/api/ApiConstants';
import { getGeoLogTags } from 'core/service/measurements/measurementUtils';
import { SearchSubErrorCode } from 'gql/autogen/SearchSubErrorCode';
import { SearchErrorCode } from 'gql/autogen/SearchErrorCode';
import { LoyaltySearchType } from 'core/type/LoyaltySearch.types';
import { SearchRequestParameters } from 'gql/autogen/SearchRequestParameters';
import { FilterCode } from 'gql/autogen/FilterCode';
import { SortCode } from 'gql/autogen/SortCode';
import { SearchType } from 'gql/autogen/SearchType';
import { Sort } from 'gql/autogen/Sort';

export const buildSearchRequestProps = (props: SearchRequestProps): SearchRequestProps => {
    const search = DefaultRequestProps.search;
    if (!props || !props.pagination) {
        return search;
    }
    return {
        searchType: props.searchType,
        searchRequestType: props.searchRequestType,
        pagination: {
            number: props.pagination.number || search.pagination.number,
            size: props.pagination.size || search.pagination.size,
        },
        cityId: props.cityId,
        keyword: props.keyword || search.keyword,
        filterActivityId: props.filterActivityId,
        filterType: props.filterType,
        filterCategory: props.filterCategory,
        defaultSort: props.defaultSort,
    };
};

const createErrorResponse = (message: string): Required<GqlResponseError> => {
    return {
        status: 400,
        shouldRetry: false,
        message,
        errorCode: SearchErrorCode.VALIDATION_ERROR,
        subErrorCode: SearchSubErrorCode.INVALID_DATA,
    };
};

export const validateSearchRequest = (request?: Record<string, unknown>): Required<GqlResponseError> | undefined => {
    if (!request || typeof request.SearchRequest !== 'object') {
        return undefined;
    }

    const searchRequest = (request.SearchRequest as SearchRequest).searchRequest;

    if (!searchRequest.searchType) {
        return createErrorResponse('Invalid searchType.');
    }

    const searchValue = searchRequest.searchValue;

    switch (searchRequest.searchType) {
        case SearchType.CITY:
        case SearchType.POINT_OF_INTEREST_BY_CITY:
            if (!searchValue || parseInt(searchValue, 10) <= 0) {
                return createErrorResponse(`Invalid value for searchValue. Found: ${searchValue}`);
            }
            break;

        case SearchType.TEXT:
            if (!searchValue) {
                return createErrorResponse("Invalid value for searchValue. searchValue shouldn't be empty.");
            }
            break;

        default:
            return createErrorResponse('Unknown searchType.');
    }

    return undefined;
};

const resolveSearchValue = (props: SearchRequestProps, state: RootState): string => {
    switch (props.searchType) {
        case SearchType.CITY:
        case SearchType.POINT_OF_INTEREST_BY_CITY: {
            const cityIdFromUrl = mapCityIdForSearch(state.bootstrap.config);
            const cityId = props.cityId || (state.bootstrap.config.isMockData ? cityIdFromUrl : (state.bootstrap.config.geo?.cityId ?? cityIdFromUrl));
            return String(cityId);
        }
        case SearchType.TEXT:
            return props.keyword || '';
        default:
            return '';
    }
};

const applySortToSearchRequest = (searchRequest: SearchRequestParameters, sort: Sort): SearchRequestParameters => {
    return {
        ...searchRequest,
        searchCriteria: {
            ...searchRequest.searchCriteria,
            sort,
        },
    };
};

const resolveDefaultSort = (currentSort: Sort, defaultSort: Sort): Sort => {
    // Use specific default sort if the current sort is the Default one otherwise its based on user selection
    if (currentSort.code === SortCode.Default) {
        return defaultSort;
    }
    return currentSort;
};

const resolveSortCodeAndOrder = (searchRequestType: SearchRequestType, props: SearchRequestProps, currentSort: Sort): Sort => {
    switch (searchRequestType) {
        case SearchRequestType.POINT_OF_INTEREST:
            return props.defaultSort;
        case SearchRequestType.POPULAR_ACTIVITIES:
            return props.defaultSort;
        case SearchRequestType.DEFAULT:
        case SearchRequestType.IN_CITY_SEARCH:
            return resolveDefaultSort(currentSort, props.defaultSort);
        default:
            return currentSort;
    }
};

const enrichInCitySearchKeyword = (searchRequest: SearchRequestParameters, props: SearchRequestProps): SearchRequestParameters => {
    const sort = resolveSortCodeAndOrder(SearchRequestType.IN_CITY_SEARCH, props, searchRequest.searchCriteria.sort);
    const withSort = applySortToSearchRequest(searchRequest, sort);
    if (props.keyword) {
        return {
            ...withSort,
            searchCriteria: {
                ...withSort.searchCriteria,
                filters: {
                    ...withSort.searchCriteria.filters,
                    valueFilters: withSort.searchCriteria.filters.valueFilters.concat({
                        code: FilterCode.ActivityContent,
                        values: [props.keyword],
                    }),
                },
            },
        };
    }
    return withSort;
};

const enrichPopularActivities = (searchRequest: SearchRequestParameters, props: SearchRequestProps): SearchRequestParameters => {
    const sort = resolveSortCodeAndOrder(SearchRequestType.POPULAR_ACTIVITIES, props, searchRequest.searchCriteria.sort);
    return applySortToSearchRequest(searchRequest, sort);
};

const enrichDefaultSearch = (searchRequest: SearchRequestParameters, props: SearchRequestProps): SearchRequestParameters => {
    const sort = resolveSortCodeAndOrder(SearchRequestType.DEFAULT, props, searchRequest.searchCriteria.sort);
    return applySortToSearchRequest(searchRequest, sort);
};

const enrichPointOfInterest = (searchRequest: SearchRequestParameters, props: SearchRequestProps): SearchRequestParameters => {
    const sort = resolveSortCodeAndOrder(SearchRequestType.POINT_OF_INTEREST, props, searchRequest.searchCriteria.sort);
    const withSort = applySortToSearchRequest(searchRequest, sort);
    return {
        ...withSort,
        searchCriteria: {
            ...withSort.searchCriteria,
            filters: {
                ...withSort.searchCriteria.filters,
                valueFilters: withSort.searchCriteria.filters.valueFilters.concat({
                    code: FilterCode.PointOfInterest,
                    values: ['ALL'],
                }),
            },
        },
    };
};

const enrichFilterActivityIdAndCategory = (searchRequest: SearchRequestParameters, props: SearchRequestProps, operationKey?: OperationKey): SearchRequestParameters => {
    if (props.filterType && props.filterActivityId && props.filterCategory) {
        return {
            ...searchRequest,
            searchCriteria: {
                ...searchRequest.searchCriteria,
                filters: {
                    ...searchRequest.searchCriteria.filters,
                    valueFilters: [
                        ...searchRequest.searchCriteria.filters.valueFilters.concat(
                            {
                                filterType: props.filterType,
                                code: FilterCode.ActivityId,
                                values: [props.filterActivityId],
                            },
                            {
                                code: FilterCode.Category,
                                values: [props.filterCategory],
                            },
                        ),
                    ],
                },
            },
        };
    }
    return searchRequest;
};

const enrichSearchRequest: Record<SearchRequestType, (searchRequest: SearchRequestParameters, props: SearchRequestProps) => SearchRequestParameters> = {
    [SearchRequestType.DEFAULT]: enrichDefaultSearch,
    [SearchRequestType.POPULAR_ACTIVITIES]: enrichPopularActivities,
    [SearchRequestType.IN_CITY_SEARCH]: enrichInCitySearchKeyword,
    [SearchRequestType.POINT_OF_INTEREST]: enrichPointOfInterest,
    [SearchRequestType.GLOBAL_SEARCH]: (sr) => sr,
};

const build = (state: RootState, props: SearchRequestProps, context: InternalContext): SearchRequest => {
    const activityId = state.search.selectedActivity;
    const loyaltySearchType = state.bootstrap.loyaltySearchType;

    const searchRequest: SearchRequestParameters = enrichFilterActivityIdAndCategory(
        {
            loyaltySearchType,
            searchType: props.searchType,
            searchValue: resolveSearchValue(props, state),
            searchCriteria: {
                activityId,
                pagination: {
                    size: props.pagination.size,
                    number: props.pagination.number,
                },
                sort: {
                    code: state.search.sort.code,
                    order: state.search.sort.order,
                },
                filters: {
                    valueFilters: state.search.filter.valueFilterMatrix,
                    rangeFilters: state.search.filter.rangeFilterMatrix,
                },
            },
        },
        props,
    );

    if (loyaltySearchType === LoyaltySearchType.UNKNOWN) {
        delete searchRequest.loyaltySearchType;
    }

    return {
        context,
        searchRequest: enrichSearchRequest[props.searchRequestType](searchRequest, props),
    };
};

const resolveCityId = (request: SearchRequestParameters): string => {
    switch (request.searchType) {
        case SearchType.CITY:
            return String(request.searchValue);
        default:
            return '';
    }
};

const resolveSearchKeyword = (request: SearchRequestParameters): string => {
    switch (request.searchType) {
        case SearchType.CITY:
            return request.searchCriteria?.filters?.valueFilters?.find((filter) => filter.code === FilterCode.ActivityContent)?.values?.[0] || '';
        case SearchType.TEXT:
            return request.searchValue;
        default:
            return '';
    }
};

export const buildLogTags = (state: RootState, variables: Record<string, unknown>): GqlLogTags => {
    const request = (variables as unknown as { SearchRequest: SearchRequest }).SearchRequest.searchRequest;
    const searchCriteria = request.searchCriteria;
    const geoTags = getGeoLogTags(state.bootstrap.config);
    return {
        ...geoTags,
        operation: ActivityGqlOperation.search,
        selectedActivity: searchCriteria.activityId === undefined ? '' : String(searchCriteria.activityId),
        searchType: request.searchType,
        cityId: resolveCityId(request),
        pageSize: String(searchCriteria.pagination.size),
        pageNumber: String(searchCriteria.pagination.number),
        sortCode: String(searchCriteria.sort.code),
        sortOrder: String(searchCriteria.sort.order),
        isPopularActivities: String(request.searchCriteria.sort.code === SortCode.Recommended),
        keyword: resolveSearchKeyword(request),
    };
};

export default build;
